import { config } from "../config";
import { callGateway, type CallerIdentity } from "./gateway";
import { ApiError } from "./errors";

/**
 * The operator's price list, READ-ONLY: the billable components radbilling holds
 * for a namespace and the price rules that price them.
 *
 *   GET /pricelist/{ns}/components   radbilling GET /componentapi/{ns}
 *   GET /pricelist/{ns}/rules        radbilling GET /priceruleapi/{ns}
 *
 * Grant: pricelist:read -- superadmin and Operator -- at radaccess v1.81
 * (feat/pricelist-read). No write exists on that resource: prices are written by
 * the compiler from the operator's approved price file, never from this screen
 * (owner 2026-09-28, radius-wiki ankhos-plan-builder-design.md §9.6-9.7).
 */

/** One component as radbilling model.Component serialises it; the fields shown. */
export interface PriceComponent {
  id: string;
  namespace: string;
  name: string;
  category: string;
  billingtype: string;
  /** Net of GST, in rupees. */
  price: number;
  gstpercent: number;
  hsnsac?: string;
  uqc?: string;
  pureinternetservice: boolean;
  radcatalog_groupuid?: string;
  radcatalog_groupname?: string;
  /** What a rule's label selector matches, e.g. {product: "residential", speed_mbps: "50", term_days: "30"}. */
  labels?: Labels | null;
  active: boolean;
  /** `<file>#<block>@<sha>` when the compiler wrote it; empty when typed in by hand. */
  catalogref?: string;
}

/** radbilling stores labels as a flat string map. */
export type Labels = Record<string, string>;

/** One price rule as radbilling model.PriceRule serialises it; the fields shown. */
export interface PriceRule {
  id: string;
  namespace: string;
  componentid?: string;
  /** Prices every component whose labels carry all of these. The compiler writes rules this way, never by id. */
  labelselector?: Labels | null;
  parentcomponentid?: string;
  /** The rule applies only on a line under a parent whose labels carry all of these. */
  parentlabelselector?: Labels | null;
  pricingmode: string;
  amount: number;
  includedquantity?: number;
  effectivefrom: string;
  effectiveto?: string;
  version: number;
  catalogref?: string;
  active: boolean;
}

const enc = encodeURIComponent;

/**
 * Is this radbilling saying "this namespace has no components", rather than a
 * route the gateway does not publish?
 *
 * 🪤 radbilling GET /componentapi/{ns} answers an EMPTY namespace with
 * 404 {"error": " Records Not Found"} (model.ListComponents), not 200 [].
 * Read as a plain 404 it told the owner "the gateway does not publish the
 * price list yet" for net.ankhnet.lotus on 2026-10-09 -- the request had in
 * fact passed the gateway and reached radbilling. Matched on the exact
 * message: the same handler answers 404 for a database error too, and that
 * must stay an error, not become an empty list.
 */
export function isNoComponents(e: unknown): boolean {
  if (!(e instanceof ApiError) || e.kind !== "not-found") return false;
  const body = e.body as { error?: unknown } | null;
  return typeof body?.error === "string" && body.error.trim() === "Records Not Found";
}

/** An empty namespace is [] here, whichever way radbilling says it. */
export async function listPriceComponents(identity: CallerIdentity, ns: string, signal?: AbortSignal): Promise<PriceComponent[]> {
  try {
    const r = await callGateway<PriceComponent[] | null>(identity, config().gatewayBase, `/pricelist/${enc(ns)}/components`, signal ? { signal } : {});
    return r.data ?? [];
  } catch (e) {
    if (isNoComponents(e)) return [];
    throw e;
  }
}

export async function listPriceRules(identity: CallerIdentity, ns: string, signal?: AbortSignal): Promise<PriceRule[]> {
  const r = await callGateway<PriceRule[] | null>(identity, config().gatewayBase, `/pricelist/${enc(ns)}/rules`, signal ? { signal } : {});
  return r.data ?? [];
}

/** The price list billing holds on a day, in the approved format (owner 2026-10-28, step 2). */
export interface PriceListExport {
  namespace: string;
  /** India date, YYYY-MM-DD. */
  on: string;
  filename: string;
  /** null when the file cannot be written; differences say why. */
  text: string | null;
  sha256: string | null;
  /** Prices billing charges that the file does not reproduce. Empty: uploading it back changes nothing. */
  differences: string[];
  /** Charges not in the file, and why. */
  left_out: string[];
  /** Changes that start after the day: not in the file yet. */
  pending: { id: string; labels: Labels; amount: number; from: string }[];
}

export async function exportPriceList(identity: CallerIdentity, ns: string): Promise<PriceListExport> {
  const r = await callGateway<PriceListExport>(identity, config().gatewayBase, `/pricelist/${enc(ns)}/export`);
  return r.data;
}

/**
 * What one line charges (owner 2026-10-30, radbilling v0.1.125): net plus GST
 * to the paisa, then the nearest rupee, .50 and above up. The same two steps as
 * radbilling's wholeRupeeTax, with or without GST: rounding only once would
 * give ₹83 where billing charges ₹84 for a gross of 83.496. Quantity above 1:
 * use the quote's amount_gross, which rounds the whole amount.
 */
export function rupeeGross(net: number, gstPercent: number): number {
  return Math.round(Math.round(net * (1 + gstPercent / 100) * 100) / 100);
}

/**
 * What the subscriber pays. Shown beside the net figure, never instead of it --
 * the net is what is stored, and a list that showed only the gross would hide
 * the one number radbilling actually holds.
 */
export function grossOf(net: number, gstPercent: number): number {
  return rupeeGross(net, gstPercent);
}

/** Where a price came from, in words an operator reads. */
export function sourceOf(c: Pick<PriceComponent, "catalogref">): "compiled" | "typed by hand" {
  return c.catalogref && c.catalogref.trim() ? "compiled" : "typed by hand";
}

export interface PriceListRow {
  component: PriceComponent;
  gross: number;
  rules: PriceRule[];
}

/**
 * Does every key of `selector` appear in `labels` with the same value?
 * An empty or missing selector matches NOTHING: a rule naming no labels
 * selects no component (radbilling model.selectorMatches treats it the same way).
 */
export function labelsMatch(selector: Labels | null | undefined, labels: Labels | null | undefined): boolean {
  const keys = selector ? Object.keys(selector) : [];
  if (keys.length === 0 || !labels) return false;
  return keys.every((k) => labels[k] === selector![k]);
}

/** Does this rule price this component -- by its id, or by a label selector its labels satisfy? */
export function rulePrices(r: PriceRule, c: PriceComponent): boolean {
  if (r.componentid) return r.componentid === c.id;
  return labelsMatch(r.labelselector, c.labels);
}

/** Is this rule priced only on a line under a parent? */
export function isParentScoped(r: PriceRule): boolean {
  return Boolean(r.parentcomponentid) || Object.keys(r.parentlabelselector ?? {}).length > 0;
}

/**
 * One row per component, its rules attached, newest rule first. Active
 * components before withdrawn ones, then by name, so the list reads the same
 * on every load. A rule for a component this namespace does not list (a
 * rule for a component elsewhere, or a selector no component here matches) is returned apart,
 * never dropped: a price that silently disappears from the list is the error
 * this screen exists to make visible.
 */
export function priceListRows(components: PriceComponent[], rules: PriceRule[]): { rows: PriceListRow[]; unattached: PriceRule[] } {
  // 🪤 Matching by componentid alone put EVERY compiled rule here: the
  // price-list compiler writes rules by label selector, never by id
  // (operator-wiki tools/compile_pricelist.py).
  const byComponent = new Map<string, PriceRule[]>();
  const unattached: PriceRule[] = [];
  for (const r of rules) {
    const priced = components.filter((c) => rulePrices(r, c));
    if (priced.length === 0) unattached.push(r);
    for (const c of priced) byComponent.set(c.id, [...(byComponent.get(c.id) ?? []), r]);
  }
  const newestFirst = (a: PriceRule, b: PriceRule) => b.effectivefrom.localeCompare(a.effectivefrom);
  const rows = [...components]
    .sort((a, b) => Number(b.active) - Number(a.active) || a.name.localeCompare(b.name))
    .map((component) => ({
      component,
      gross: grossOf(component.price, component.gstpercent),
      rules: [...(byComponent.get(component.id) ?? [])].sort(newestFirst),
    }));
  return { rows, unattached: unattached.sort(newestFirst) };
}

/** 30 -> "1 month", 90 -> "3 months", 365 -> "12 months", 7 -> "7 days". */
export function termWords(days: number): string {
  const months: Record<number, number> = { 30: 1, 90: 3, 180: 6, 365: 12 };
  const m = months[days];
  if (m) return `${m} month${m === 1 ? "" : "s"}`;
  return `${days} day${days === 1 ? "" : "s"}`;
}

/**
 * Which plans a rule's selector picks, in words: {"product":"residential",
 * "speed_mbps":"30","term_days":"90"} -> "Residential 30 Mbps · 3 months".
 * kind/addon only name the charge itself, so they are left out.
 */
export function describeSelector(sel: Labels | null | undefined): string {
  if (!sel) return "All plans";
  const parts: string[] = [];
  if (sel.product) parts.push(sel.product[0]!.toUpperCase() + sel.product.slice(1));
  if (sel.speed_mbps) parts.push(`${sel.speed_mbps} Mbps`);
  const head = parts.join(" ");
  const days = Number(sel.term_days);
  const term = sel.term_days && Number.isFinite(days) ? termWords(days) : "";
  const rest = Object.entries(sel)
    .filter(([k]) => !["product", "speed_mbps", "term_days", "kind", "addon"].includes(k))
    .map(([k, v]) => `${k} ${v}`);
  const out = [head, term, ...rest].filter(Boolean).join(" · ");
  return out || "All plans";
}

/**
 * The GST each active component is sold at (owner 2026-10-29): radbilling's
 * quote, per line, so the SUPPLIER OF RECORD's regime -- a registered seller
 * 18%, an unregistered or composition seller 0 -- never the operating
 * namespace's own taxinfo. radaccess GET /access/pricelist/{ns}/gst.
 * A component not in `gst` has no known rate: it is shown "—", never 18.
 */
export interface SellerGst {
  gst: Record<string, number>;
  unquoted?: Record<string, string>;
  /**
   * The line's seller would have its invoice refused (owner 2026-10-29): a
   * GST-registered seller with no GSTIN, or a refusal from invoice creation's gates.
   */
  warnings?: Record<string, string>;
  /**
   * The viewed operator's own registration. Owner 2026-10-29: "If operator is
   * unregister or in composition scheme. No word of GST should appear."
   */
  registrationstatus?: string;
}

/** Does this operator's screen speak of GST at all? Not for UNREGISTERED or COMPOSITION; yes when unknown. */
export function speaksGst(s: Pick<SellerGst, "registrationstatus"> | undefined): boolean {
  const r = (s?.registrationstatus ?? "").toUpperCase();
  return r !== "UNREGISTERED" && r !== "COMPOSITION";
}

export async function listSellerGst(identity: CallerIdentity, ns: string, signal?: AbortSignal): Promise<SellerGst> {
  const r = await callGateway<SellerGst | null>(identity, config().gatewayBase, `/pricelist/${enc(ns)}/gst`, signal ? { signal } : {});
  return {
    gst: r.data?.gst ?? {},
    ...(r.data?.unquoted ? { unquoted: r.data.unquoted } : {}),
    ...(r.data?.warnings ? { warnings: r.data.warnings } : {}),
    ...(r.data?.registrationstatus ? { registrationstatus: r.data.registrationstatus } : {}),
  };
}

/** Why this line's invoice would be refused, or undefined. */
export function sellerWarningOf(c: Pick<PriceComponent, "id">, s: SellerGst | undefined): string | undefined {
  return s?.warnings?.[c.id] || undefined;
}

/** The short form shown beside a price; the full reason goes in the tooltip. */
export function warningWords(w: string): string {
  return w.startsWith("seller GST details incomplete") ? "seller GST details incomplete" : "invoice would be refused";
}

/** The component's rate, or undefined when billing did not state one. */
export function sellerGstOf(c: Pick<PriceComponent, "id">, s: SellerGst | undefined): number | undefined {
  const v = s?.gst[c.id];
  return typeof v === "number" ? v : undefined;
}

const inr2 = (n: number) => `₹${n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const inr0 = (n: number) => `₹${n.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

/**
 * What the subscriber pays, worded as the owner set it (2026-10-29): a
 * registered seller "₹30 incl. 18% GST"; unregistered or composition "₹30, no GST".
 */
export function priceWords(gross: number, gstPercent: number, speak = true): string {
  if (!speak) return inr0(gross);
  return gstPercent > 0 ? `${inr0(gross)} incl. ${gstPercent}% GST` : `${inr0(gross)}, no GST`;
}

/** Net to what the subscriber pays: whole rupees, with or without GST. */
export function sellerGross(net: number, gstPercent: number): number {
  return rupeeGross(net, gstPercent);
}

/** A rule's price as the subscriber pays it: GST added to an ABSOLUTE net amount, in whole rupees. */
export function rulePays(r: Pick<PriceRule, "pricingmode" | "amount">, gstPercent: number | undefined, speak = true): string {
  const inr = inr2;
  switch (r.pricingmode) {
    case "FREE":
      return "Free";
    case "PERCENT_OF_PARENT":
      return `${r.amount}% of the plan`;
    case "ABSOLUTE": {
      if (!speak) {
        // What the subscriber pays, and nothing about tax (owner 2026-10-29).
        return gstPercent === undefined ? "price not available" : inr0(rupeeGross(r.amount, gstPercent));
      }
      if (gstPercent === undefined) return `${inr(Math.round(r.amount * 100) / 100)} net (GST not known)`;
      if (gstPercent === 0) return `${inr0(rupeeGross(r.amount, 0))} (no GST)`;
      return `${inr0(rupeeGross(r.amount, gstPercent))} (${inr(Math.round(r.amount * 100) / 100)} + GST)`;
    }
    default:
      return `${r.pricingmode} ${inr(r.amount)}`;
  }
}

/** Sort key: product, then speed, then days. */
export function selectorOrder(a: Labels | null | undefined, b: Labels | null | undefined): number {
  const n = (s: Labels | null | undefined, k: string) => Number(s?.[k] ?? 0) || 0;
  return (a?.product ?? "").localeCompare(b?.product ?? "") || n(a, "speed_mbps") - n(b, "speed_mbps") || n(a, "term_days") - n(b, "term_days");
}

/**
 * Whether a component is an add-on the operator may tick internet or not:
 * labels.kind "addon" and no plan link. A plan is always internet service.
 */
export const isTickableAddon = (c: PriceComponent): boolean => c.labels?.kind === "addon" && !c.radcatalog_groupuid && c.active;

/**
 * The plan builder's "Internet service" tick (owner 2026-10-17): an add-on
 * that is not internet goes on the sale's separate receipt.
 * PUT /access/operator/{ns}/components/{id}/internet -> radbilling.
 */
/**
 * Link an add-on to the item its upstream prices in the wallet ("" unlinks;
 * owner 2026-10-18). radaccess refuses 409 unless a seller prices the item,
 * or while a sale holds the add-on.
 */
export async function setComponentUpstreamItem(identity: CallerIdentity, ns: string, componentId: string, upstreamItem: string): Promise<PriceComponent> {
  const r = await callGateway<PriceComponent>(identity, config().gatewayBase, `/operator/${enc(ns)}/components/${enc(componentId)}/upstream`, {
    method: "PUT",
    body: { upstream_item: upstreamItem },
  });
  return r.data;
}

export async function setComponentInternet(identity: CallerIdentity, ns: string, componentId: string, internet: boolean): Promise<PriceComponent> {
  const r = await callGateway<PriceComponent>(identity, config().gatewayBase, `/operator/${enc(ns)}/components/${enc(componentId)}/internet`, {
    method: "PUT",
    body: { internet },
  });
  return r.data;
}

/**
 * Change price (owner 2026-10-28, price-list idempotency step 1): change ONE
 * price from a date, with no document and no model.
 *
 *   POST /access/operator/{ns}/pricerules/reprice -> radbilling POST /priceruleapi/{ns}/reprice
 *
 * radbilling ends the rule at `effectivefrom` and starts its successor in one
 * transaction; the same component + amount + date again changes nothing.
 * `amount` is what the rule stores: NET of GST (radaccess v1.199+, radbilling v0.1.113+).
 */
export interface RepriceInput {
  ruleid: string;
  /** Net of GST, 4 decimals -- see netOfGross. */
  amount: number;
  /** "YYYY-MM-DD", an Indian calendar date from tomorrow on. */
  effectivefrom: string;
}

export interface RepricedRule {
  id: string;
  amount: number;
  effectivefrom: string;
  effectiveto?: string;
}

export interface RepriceResult {
  ended?: RepricedRule;
  created?: RepricedRule;
  /** The amount was already the rule's: nothing written. */
  unchanged: boolean;
  /** This exact change was made before: the same pair, nothing written again. */
  replay?: boolean;
}

export async function repricePriceRule(identity: CallerIdentity, ns: string, input: RepriceInput): Promise<RepriceResult> {
  const r = await callGateway<RepriceResult>(identity, config().gatewayBase, `/operator/${enc(ns)}/pricerules/reprice`, {
    method: "POST",
    body: input,
  });
  return r.data;
}

/** Move or cancel a change that has not started (owner 2026-10-28). */
export interface PendingChangeInput {
  /** The rule Change price created, which starts in the future. */
  ruleid: string;
  action: "move" | "cancel";
  /** "YYYY-MM-DD", move only: the new start, from tomorrow on. */
  effectivefrom?: string;
}

export interface PendingChangeResult {
  /** The pending rule, now withdrawn. */
  withdrawn?: RepricedRule;
  /** The old price: open again (cancel) or ending at the new date (move). */
  predecessor?: RepricedRule;
  /** move only: the same price, starting at the new date. */
  created?: RepricedRule;
  replay?: boolean;
}

export async function changePendingPrice(identity: CallerIdentity, ns: string, input: PendingChangeInput): Promise<PendingChangeResult> {
  const r = await callGateway<PendingChangeResult>(identity, config().gatewayBase, `/operator/${enc(ns)}/pricerules/reprice/pending`, {
    method: "POST",
    body: input,
  });
  return r.data;
}

/** Undo a change that has started (owner 2026-10-29): from a date on or after its start -- past allowed. */
export interface RevertInput {
  /** The change to undo: the rule Change price created. */
  ruleid: string;
  /** "YYYY-MM-DD", India: on or after the change's start. */
  effectivefrom: string;
}

export interface RevertResult {
  withdrawn?: RepricedRule;
  predecessor?: RepricedRule;
  created?: RepricedRule;
  replay?: boolean;
}

export async function revertPriceChange(identity: CallerIdentity, ns: string, input: RevertInput): Promise<RevertResult> {
  const r = await callGateway<RevertResult>(identity, config().gatewayBase, `/operator/${enc(ns)}/pricerules/reprice/revert`, {
    method: "POST",
    body: input,
  });
  return r.data;
}

/** A started fixed price that Change price made (catalog_ref …@reprice-<date>): it can be reverted. */
export function isRevertibleChange(r: PriceRule, now: Date): boolean {
  return r.active && r.pricingmode === "ABSOLUTE" && /@reprice-\d{4}-\d{2}-\d{2}$/.test(r.catalogref ?? "") &&
    istDay(new Date(r.effectivefrom)) <= istDay(now);
}

/** A fixed price that starts after today in India: Change price made it, and it can still be moved or cancelled. */
export function isPendingChange(r: PriceRule, now: Date): boolean {
  return r.active && r.pricingmode === "ABSOLUTE" && istDay(new Date(r.effectivefrom)) > istDay(now);
}

/**
 * The net a rule stores for a price the operator states with GST: gross /
 * (1 + GST), to 4 decimals, half up -- exactly as the price-list compiler
 * stores it (operator-wiki compile_pricelist.py amounts()), so a price typed
 * here and the same price compiled from a file are the same number.
 */
export function netOfGross(gross: number, gstPercent: number): number {
  return Math.round((gross * 1_000_000) / (100 + gstPercent)) / 10_000;
}

/** Can this rule be changed here: an open, active, fixed-amount rule. */
export function canChangePrice(r: PriceRule): boolean {
  return r.active && r.pricingmode === "ABSOLUTE" && !r.effectiveto;
}

/** Today in India, "YYYY-MM-DD", plus `days`. */
export function istDay(now: Date, days = 0): string {
  const d = new Date(now.getTime() + days * 86_400_000);
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Kolkata", year: "numeric", month: "2-digit", day: "2-digit" }).format(d);
}

/** What the person typed, checked before sending: a price above ₹0 and a date from tomorrow on. */
export function repriceProblems(grossText: string, from: string, now: Date): string[] {
  const out: string[] = [];
  const t = grossText.trim();
  // Owner 2026-10-30: whole rupees only. No word of GST: this reads for every operator.
  if (!/^\d+$/.test(t)) out.push("Enter the new price in whole rupees, with no paise");
  else if (Number(t) <= 0) out.push("The new price must be above ₹0");
  if (!/^\d{4}-\d{2}-\d{2}$/.test(from)) out.push("Choose the date the new price starts");
  else if (from < istDay(now, 1)) out.push("A price changes from tomorrow at the earliest");
  return out;
}
